import { BaseUserProfile, ProgramType } from 'fitbeat.models';
import { MealPreferences } from 'fitbeat.models';
import { FeatureToggle } from '../ui/components';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';
import { LongTermGoalTile } from '../auth/components';
import { LongTermGoalDescription } from '../auth/components/LongTermGoalDescription';
import { OnBoardingHeader } from '../auth/components/OnBoardingHeader';
import { Button, ButtonTypes } from '../components';
import SetupScreen from '../onboarding/SetupScreen';
import Theme, { ThemeType } from '../theme';
import { showError } from '../ui';
import { Logger, UserDataConsumer } from '../utils';
import { mappedIcons } from '../WorkoutGoalAssistant/utils';
import { defaultScale } from '../utils/scaling';

interface IState {
  longTermGoal?: ProgramType;
  isLoading: boolean;
}

const updateGoalData = {
  steps: [
    {
      visible: false,
      description: `Workouts`,
      timeoutValue: 1000,
    },
    {
      visible: false,
      description: 'Nutrition',
      timeoutValue: 3000,
    },
    {
      visible: false,
      description: 'Tracking',
      timeoutValue: 5000,
    }],
};

class FitnessGoal extends Component<any, IState> {
  public state = {
    longTermGoal: undefined,
    isLoading: false,
  };

  public viewGoalDescription = (longTermGoal: ProgramType) => {
    this.setState({ longTermGoal });
  }

  public backToGoalList = () => {
    this.setState({ longTermGoal: undefined });
  }

  public saveGoal = async () => {
    try {
      const { longTermGoal } = this.state;
      this.setState({ isLoading: true });
      this.props.setSetupScreen(false);

      const { userProfile, userProfileService, userServiceClient } = this.props.user;

      if (userProfile.longTermGoal !== longTermGoal) {
        await userServiceClient.resetTargetForUser();
        this.props.userGoalChanged();
      }

      userProfile.longTermGoal = longTermGoal;
      if (!userProfile.mealPreferences) {
        userProfile.mealPreferences = new MealPreferences();
      }

      if (longTermGoal === ProgramType.MobilityAndTone) {
        userProfile.mealPreferences.dietGoal = ProgramType.GetLeanAndFitGoal;
      } else if (longTermGoal === ProgramType.MuscleAndShred) {
        userProfile.mealPreferences.dietGoal = ProgramType.GetStrongAndFitGoal;
      } else {
        userProfile.mealPreferences.dietGoal = longTermGoal;
      }

      await userProfileService.updateUserProfile(userProfile);

      this.props.setSetupScreen(true);
    } catch (error) {
      showError('Oops, something went wrong.');
      Logger.notify(error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  public renderGoalList = () => {
    const { userProfile } = this.props.user;
    return (
      <View style={styles.listContainer}>
        <LongTermGoalTile
          useInline
          shouldHightLightSelected
          theme={ThemeType.light}
          currentSelectedGoal={this.props.currentSelectedGoal}
          programType={ProgramType.GetStrongAndFitGoal}
          onPress={() => this.viewGoalDescription(ProgramType.GetStrongAndFitGoal)}
        />
        <LongTermGoalTile
          useInline
          shouldHightLightSelected
          theme={ThemeType.light}
          currentSelectedGoal={this.props.currentSelectedGoal}
          programType={ProgramType.GetLeanAndFitGoal}
          onPress={() => this.viewGoalDescription(ProgramType.GetLeanAndFitGoal)}
        />
        <LongTermGoalTile
          useInline
          shouldHightLightSelected
          theme={ThemeType.light}
          currentSelectedGoal={this.props.currentSelectedGoal}
          programType={ProgramType.MobilityAndTone}
          onPress={() => this.viewGoalDescription(ProgramType.MobilityAndTone)}
        />
        <LongTermGoalTile
          useInline
          shouldHightLightSelected
          theme={ThemeType.light}
          currentSelectedGoal={this.props.currentSelectedGoal}
          programType={ProgramType.GainMuscleAndStrengthGoal}
          onPress={() => this.viewGoalDescription(ProgramType.GainMuscleAndStrengthGoal)}
        />
        <LongTermGoalTile
          useInline
          shouldHightLightSelected
          theme={ThemeType.light}
          currentSelectedGoal={this.props.currentSelectedGoal}
          programType={ProgramType.MuscleAndShred}
          onPress={() => this.viewGoalDescription(ProgramType.MuscleAndShred)}
        />
      </View>
    );
  }

  public renderGoalDescription = () => {
    const { t } = this.props;
    const { longTermGoal, isLoading } = this.state;
    return (
      <>
        <View style={{ flex: 1, marginHorizontal: defaultScale(-20), }}>
          <LongTermGoalDescription
            theme={ThemeType.light}
            longTermGoal={longTermGoal}
            goalTileContainerStyle={styles.goalTileContainer}
          />
        </View>
        <View style={styles.footer}>
          {isLoading ?
            <ActivityIndicator animating color={Theme.PRIMARY} style={styles.loader} />
            :
            <View style={{ flexDirection: 'row', width: '100%' }}>
              <Button
                title={t('bookings:back')}
                isLoading={isLoading}
                fontColor={Theme.PRIMARY}
                containerStyle={{ flex: 0.3, marginRight: defaultScale(5) }}
                onPress={this.backToGoalList}
                buttonType={ButtonTypes.OUTLINE}
              />
              <Button
                title={t('update')}
                isLoading={isLoading}
                fontColor={Theme.GREY_LIGHT}
                containerStyle={{ flex: 0.7 }}
                onPress={this.saveGoal}
                buttonType={ButtonTypes.NORMAL}
              />
            </View>
          }
        </View>
      </>
    );
  }

  public renderSetupDone = () => {
    return (
      <SetupScreen setupType={'updating fitness goal'}
        user={this.props.user.userProfile} setupData={updateGoalData}
        savePreferences={this.closeModal}
        image={<View style={styles.logo}>
          {mappedIcons[this.props.user.userProfile.longTermGoal]({}, Theme.BLACK)}
        </View>}
      />
    );
  }

  public closeModal = () => {
    this.props.setSetupScreen(false);
    this.setState({ longTermGoal: undefined, isLoading: false });
    this.props.closeWorkoutProgramDescription();
    this.props.completeGoalChange();
  }

  public render() {
    const { isSetupScreen } = this.props;
    const { longTermGoal } = this.state;
    if (isSetupScreen) {
      return this.renderSetupDone();
    }
    return longTermGoal ? this.renderGoalDescription() : this.renderGoalList();
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.WHITE,
    paddingHorizontal: defaultScale(15),
    paddingBottom: defaultScale(15),
    ...Platform.select({
      web: {
        paddingHorizontal: 30,
        paddingVertical: 15,
      },
    }),
  },
  header: {
    ...Theme.textStyle.bodyCopyMed,
    color: '#282828',
    ...Platform.select({
      web: {
        marginLeft: 20,
      },
    }),
  },
  subHeader: {
    color: '#424242',
    fontSize: 13,
    fontFamily: Theme.Custom500,
    letterSpacing: 0,
    lineHeight: 16,
    ...Platform.select({
      web: {
        marginBottom: 20,
        marginLeft: 20,
      },
    }),
  },
  listContainer: {
    paddingTop: defaultScale(10),
  },
  loader: {
  },
  footer: {
    paddingVertical: defaultScale(20),
  },
  logo: {
    alignSelf: 'center',
    width: defaultScale(40),
    height: defaultScale(40),
    margin: defaultScale(2.5),
    marginHorizontal: defaultScale(10),
    marginVertical: defaultScale(20),
    marginTop: defaultScale(50),
  },
  goalTileContainer: {
    paddingHorizontal: defaultScale(20),
  },
});

const FitnessGoalUser = UserDataConsumer(FitnessGoal);
const FitnessGoalTranslate = translate(['settings'], { wait: true })(FitnessGoalUser);
export default FitnessGoalTranslate;

